/*
 * nf-banner.js — Carrossel de destaque (NF).
 * ---------------------------------------------------------------------------
 * Custom element <nf-carousel>:
 *  - navegacao por setas, pontos (dots), teclado (setas) e swipe no touch;
 *  - autoplay suave (intervalo configuravel), que pausa no hover, no foco,
 *    com a aba oculta e quando a secao sai da viewport;
 *  - transicao por transform (translateX) na trilha; sob prefers-reduced-motion
 *    a classe de animacao nao e aplicada e o autoplay fica desligado — os
 *    slides continuam navegaveis pelos controles;
 *  - ARIA: aria-hidden/tabindex por slide, aria-current no dot ativo e uma
 *    regiao aria-live que anuncia "Slide N de M".
 * Sem dependencias externas.
 */
(function () {
  if (customElements.get('nf-carousel')) return;

  var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  class NfCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-nf-track]');
      this.viewport = this.querySelector('[data-nf-viewport]');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-nf-slide]'));
      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-nf-dot]'));
      this.prevBtn = this.querySelector('[data-nf-prev]');
      this.nextBtn = this.querySelector('[data-nf-next]');
      this.live = this.querySelector('[data-nf-live]');
      this.count = this.slides.length;

      if (!this.track || this.count <= 1) return;

      this.index = 0;
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.interval = parseInt(this.dataset.interval, 10) || 5000;
      this.timer = null;
      this.paused = false;
      this.inView = true;

      this._onPrev = this.onPrev.bind(this);
      this._onNext = this.onNext.bind(this);
      this._onKey = this.onKey.bind(this);
      this._pause = this.pause.bind(this);
      this._resume = this.resume.bind(this);
      this._onVisibility = this.onVisibility.bind(this);
      this._onMotion = this.applyMotion.bind(this);

      if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNext);
      this.dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          this.go(i);
          this.restart();
        }.bind(this));
      }.bind(this));

      this.addEventListener('keydown', this._onKey);
      this.addEventListener('mouseenter', this._pause);
      this.addEventListener('mouseleave', this._resume);
      this.addEventListener('focusin', this._pause);
      this.addEventListener('focusout', this._resume);
      document.addEventListener('visibilitychange', this._onVisibility);

      if (motionQuery.addEventListener) {
        motionQuery.addEventListener('change', this._onMotion);
      } else if (motionQuery.addListener) {
        motionQuery.addListener(this._onMotion);
      }

      this.bindSwipe();

      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver(function (entries) {
          this.inView = entries[0].isIntersecting;
          if (this.inView) this.play();
          else this.stopTimer();
        }.bind(this), { threshold: 0.4 });
        this._io.observe(this);
      }

      this.render();
      this.applyMotion();
    }

    disconnectedCallback() {
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this._onNext);
      this.removeEventListener('keydown', this._onKey);
      this.removeEventListener('mouseenter', this._pause);
      this.removeEventListener('mouseleave', this._resume);
      this.removeEventListener('focusin', this._pause);
      this.removeEventListener('focusout', this._resume);
      document.removeEventListener('visibilitychange', this._onVisibility);
      if (motionQuery.removeEventListener) {
        motionQuery.removeEventListener('change', this._onMotion);
      } else if (motionQuery.removeListener) {
        motionQuery.removeListener(this._onMotion);
      }
      if (this._io) this._io.disconnect();
      this.stopTimer();
    }

    onPrev() {
      this.go(this.index - 1);
      this.restart();
    }

    onNext() {
      this.go(this.index + 1);
      this.restart();
    }

    onKey(e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.go(this.index - 1);
        this.restart();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.go(this.index + 1);
        this.restart();
      }
    }

    onVisibility() {
      if (document.hidden) this.stopTimer();
      else this.play();
    }

    pause() {
      this.paused = true;
      this.stopTimer();
    }

    resume() {
      this.paused = false;
      this.play();
    }

    go(i) {
      this.index = (i + this.count) % this.count;
      this.render();
    }

    render() {
      var pct = this.index * 100;
      this.track.style.transform = 'translateX(-' + pct + '%)';

      this.slides.forEach(function (slide, i) {
        var active = i === this.index;
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        var focusables = slide.querySelectorAll('a, button');
        Array.prototype.forEach.call(focusables, function (el) {
          el.tabIndex = active ? 0 : -1;
        });
      }.bind(this));

      this.dots.forEach(function (dot, i) {
        var active = i === this.index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      }.bind(this));

      this.applyRatio();

      if (this.live) {
        this.live.textContent = 'Slide ' + (this.index + 1) + ' de ' + this.count;
      }
    }

    /*
     * Poe no quadro a proporcao da arte do slide ativo, para que artes de
     * tamanhos diferentes no mesmo carrossel nao gerem corte nem faixa nas
     * bordas. O CSS escolhe entre a variavel de desktop e a de celular; aqui
     * so atualizamos as duas. Slide sem imagem mantem a proporcao anterior.
     */
    applyRatio() {
      if (!this.viewport) return;

      var slide = this.slides[this.index];
      if (!slide) return;

      var ratio = slide.getAttribute('data-nf-ratio');
      if (ratio) this.viewport.style.setProperty('--nf-banner-ratio', ratio);

      var ratioMobile = slide.getAttribute('data-nf-ratio-m');
      if (ratioMobile) {
        this.viewport.style.setProperty('--nf-banner-ratio-m', ratioMobile);
      } else if (ratio) {
        this.viewport.style.setProperty('--nf-banner-ratio-m', ratio);
      }
    }

    applyMotion() {
      var reduce = motionQuery.matches;
      this.classList.toggle('nf-banner--animate', !reduce);
      if (reduce) this.stopTimer();
      else this.play();
    }

    play() {
      if (!this.autoplayEnabled) return;
      if (motionQuery.matches) return;
      if (this.paused || !this.inView) return;
      this.stopTimer();
      this.timer = window.setInterval(function () {
        this.go(this.index + 1);
      }.bind(this), this.interval);
    }

    stopTimer() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    restart() {
      this.stopTimer();
      this.play();
    }

    bindSwipe() {
      var startX = null;
      var startY = null;
      var target = this.viewport || this;

      target.addEventListener('touchstart', function (e) {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });

      target.addEventListener('touchend', function (e) {
        if (startX === null) return;
        var dx = e.changedTouches[0].clientX - startX;
        var dy = e.changedTouches[0].clientY - startY;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          if (dx < 0) this.go(this.index + 1);
          else this.go(this.index - 1);
          this.restart();
        }
        startX = null;
        startY = null;
      }.bind(this), { passive: true });
    }
  }

  customElements.define('nf-carousel', NfCarousel);
})();
